import {
    AdProvider,
    AdProviderConfig,
    AdProviderStatus,
    AdUnitProvidersConfig,
    OutOfPageAdType,
    PageAdOptions,
    PageAdType,
} from '@/modules/adv'
import { Banner, BannerState } from '@/modules/adv/banner'
import { OutOfPageBanner } from '@/modules/adv/out-of-page-banner'
import { DebugAdsProvider } from '@/modules/adv/providers/debug-provider'
import { EmptyAdsProvider } from '@/modules/adv/providers/empty-ads'
import { GameDistributionConnector } from '@/modules/adv/providers/gd-connector/gd-connector'
import { GoogleAdsService } from '@/modules/adv/providers/google-ads'
import { GoogleInstreamAdsService } from '@/modules/adv/providers/google-instream-ads'
import { AdProviderNames, PlatformType } from '@/types'
import { EventForLoggersType } from '@/types/logger.types'

import { AdServiceTargetingOptions } from './AdServiceTargetingOptions'
import { GoogleAdSenseAdProvider } from './providers/google-ad-sense-ad-provider'

export type AdServiceOptions = {
    providerKeys: AdProviderNames[]
    targeting?: AdServiceTargetingOptions
    logEvent: (event: EventForLoggersType) => void
    adRequestTimeoutMs: number
    initialDelay?: Promise<void>

    gamAccount: string
    platform: PlatformType
    auid: string
    isWidget: boolean

    adUnitProviderConfig?: Partial<AdUnitProvidersConfig<AdProviderNames>>
    footerHeight?: string
}

const PROVIDER_INITIALIZATION_TIMEOUT = 5000 // 5 seconds
const MEMBRANA_GAM = '22308795431'

export class AdService {
    private readonly options: AdServiceOptions
    private readonly logEvent: (event: EventForLoggersType) => void

    private waitingProviders: AdProvider[] | null = null
    private providers: AdProvider[] = []
    serviceReadyPromise: Promise<AdProviderStatus>

    constructor(options: AdServiceOptions) {
        this.options = options
        this.logEvent = options.logEvent

        this.serviceReadyPromise = (options.initialDelay || Promise.resolve()).then(() => this.initProviders())
    }

    private async initProviders(): Promise<AdProviderStatus> {
        this.waitingProviders = this.options.providerKeys.map((key) => this.getProvider(key, this.options))

        if (this.waitingProviders.length === 0) {
            throw new Error('No providers defined')
        }

        await Promise.all(
            this.waitingProviders.map((provider, index) =>
                Promise.race([
                    provider.serviceStatus.then((status) => {
                        if (status === AdProviderStatus.online) {
                            this.providers[index] = provider
                        }
                    }),
                    new Promise((res) => setTimeout(res, PROVIDER_INITIALIZATION_TIMEOUT)),
                ]),
            ),
        )

        if (this.providers.length === 0) {
            this.providers = [new EmptyAdsProvider(this.options)]
        }

        return AdProviderStatus.online
    }

    private getProvider(key: AdProviderNames, adServiceConfig: AdServiceOptions) {
        const PROVIDER_CREATORS: Record<AdProviderNames, () => AdProvider> = {
            [AdProviderNames.GAME_DISTRIBUTION]: () => new GameDistributionConnector(),
            [AdProviderNames.DEBUG_PROVIDER]: () => new DebugAdsProvider(),
            [AdProviderNames.EMPTY_PROVIDER]: () =>
                new EmptyAdsProvider({
                    ...adServiceConfig,
                }),
            [AdProviderNames.GOOGLE_INSTREAM]: () =>
                new GoogleInstreamAdsService({
                    ...adServiceConfig,
                    adUnitConfig: adServiceConfig.adUnitProviderConfig?.[AdProviderNames.GOOGLE_INSTREAM],
                    gamAccount: this.options.gamAccount,
                }),
            [AdProviderNames.GOOGLE_INSTREAM_CHEAP]: () =>
                new GoogleInstreamAdsService({
                    ...adServiceConfig,
                    adUnitConfig: adServiceConfig.adUnitProviderConfig?.[AdProviderNames.GOOGLE_INSTREAM_CHEAP],
                    gamAccount: this.options.gamAccount,
                }),
            [AdProviderNames.MEMBRANA]: () =>
                new GoogleAdsService({
                    ...adServiceConfig,
                    adUnitConfig: {
                        desktop: {
                            rewarded: 'PGM_REWARD',
                            interstitial: 'PGM_INTERSTITIAL',
                        },
                        mobile: {
                            rewarded: 'PGM_REWARD',
                            interstitial: 'PGM_INTERSTITIAL',
                        },
                    },
                    gamAccount: MEMBRANA_GAM,
                    useOnlyConfig: true,
                }),
            [AdProviderNames.MEMBRANA_VAST]: () =>
                new GoogleInstreamAdsService({
                    ...adServiceConfig,
                    adUnitConfig: {
                        desktop: {
                            interstitial_preroll: 'PGM_PREROLL',
                        },
                        mobile: {
                            interstitial_preroll: 'PGM_PREROLL',
                        },
                    },
                    gamAccount: MEMBRANA_GAM,
                    adUrl: 'https://server.smartytech.io/vast?id=204',
                    useOnlyConfig: true,
                }),
            [AdProviderNames.GOOGLE_AD_SENSE]: () => new GoogleAdSenseAdProvider(adServiceConfig),
            [AdProviderNames.GOOGLE_AD]: () =>
                new GoogleAdsService({
                    ...adServiceConfig,
                    adUnitConfig: adServiceConfig.adUnitProviderConfig?.[AdProviderNames.GOOGLE_AD],
                    gamAccount: this.options.gamAccount,
                }),
        }

        const providerCreator: () => AdProvider = PROVIDER_CREATORS[key] || PROVIDER_CREATORS[AdProviderNames.GOOGLE_AD]

        return providerCreator()
    }

    private requestAd<T extends Banner>(
        providerMethod: (provider: AdProvider) => Promise<T>,
        fallbackBanner: () => T,
        type: PageAdType | OutOfPageAdType,
        waterfallIndex = 0,
    ): Promise<T> {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve) => {
            try {
                if (!this.providers[waterfallIndex]) {
                    throw new Error('Provider is not initialized')
                }

                this.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_request',
                    adPlacement: type,
                    action: 'start',
                    adUnit: this.providers[waterfallIndex].getAdUnitByType(type),
                    waterfallIndex: waterfallIndex + 1,
                })

                const banner = await providerMethod(this.providers[waterfallIndex])

                banner.addEventListener('empty', () => {
                    if (waterfallIndex + 1 === this.providers.length) {
                        resolve(banner)
                    } else {
                        resolve(this.requestAd(providerMethod, fallbackBanner, type, waterfallIndex + 1))
                    }
                })

                if (banner.state === BannerState.ready) {
                    resolve(banner)
                } else if (banner.state === BannerState.closed || banner.state === BannerState.empty) {
                    throw new Error(`Ad is in state=[${banner.state}]`)
                } else {
                    banner.addEventListener('ready', () => {
                        resolve(banner)
                    })
                }

                banner.addEventListener('closed', () => {
                    resolve(banner)
                })

                // for page ad we don't have ready event
                banner.addEventListener('rendered', () => {
                    resolve(banner)
                })

                banner.addEventListener('viewable', () => {
                    this.logEvent({
                        event: 'custom_event',
                        eventName: 'ad_show',
                        adPlacement: type,
                        action: 'show',
                        adUnit: banner.adUnit,
                        instreamDuration: banner.instreamDuration,
                    })
                })
            } catch (error) {
                if (waterfallIndex + 1 === this.providers.length) {
                    const banner = fallbackBanner()
                    resolve(banner)
                    banner.triggerEmpty()

                    this.logEvent({
                        event: 'custom_event',
                        eventName: 'ad_request_failed',
                        adPlacement: type,
                        action: 'error',
                        errorMessage: error instanceof Error ? error.message : 'Cannot show the ad',
                    })
                } else {
                    resolve(this.requestAd(providerMethod, fallbackBanner, type, waterfallIndex + 1))
                }
            }
        })
    }

    async requestOutOfPageAd(
        type: OutOfPageAdType,
        timeout = this.options.adRequestTimeoutMs,
    ): Promise<OutOfPageBanner> {
        await this.serviceReadyPromise
        return this.requestAd<OutOfPageBanner>(
            (provider) => provider.prepareOutOfPageAd(type, timeout),
            () => new OutOfPageBanner({ provider: AdProviderNames.EMPTY_PROVIDER }),
            type,
        )
    }

    async requestPageAd(options: PageAdOptions): Promise<Banner> {
        await this.serviceReadyPromise
        return this.requestAd<Banner>(
            (provider) => provider.requestPageAd(options),
            () => new Banner({ provider: AdProviderNames.EMPTY_PROVIDER }),
            options.type,
        )
    }

    updateTargeting(targeting: AdProviderConfig['targeting']) {
        this.waitingProviders?.forEach(async (provider) => {
            try {
                await provider.serviceStatus
                provider.updateTargeting(targeting)
            } catch {
                // provider threw an error, nothing we can do
            }
        })
    }
}
